import { useCallback, useEffect, useMemo, useState } from 'react';
import { crmDeepLink } from '@/lib/links.js';
import { api } from '@/lib/api.js';
import { toast } from '@/components/ui/toaster.jsx';
import MoveConfirm from '@/components/money/MoveConfirm.jsx';
import { fmtCents, fmtPct } from '@/components/money/format.js';

// ALLOCATION (TRACON-MONEY-SPEC §6, R9): on the 10th and 25th, what has been
// swept but not yet moved to income. Same shape as the sweep confirm: the
// amount actually moved is typed, a mismatch is refused, allocated_at is
// stamped. The balance shown is COMPUTED from the swept rows; the bank's own
// number arrives with the bank adapter (Ship 4) and is not invented here.

export default function MoneyAllocate() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  const refresh = useCallback(async () => {
    try {
      const d = await api.get('/api/money/allocate/pending');
      setData(d);
      setSelected(new Set(d.rows.filter((r) => r.state === 'ready').map((r) => r.id)));
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load the allocation list');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const rows = data?.rows || [];
  const selectedTotal = useMemo(
    () => Math.round(rows.filter((r) => selected.has(r.id) && r.sweep_amount != null).reduce((acc, r) => acc + Number(r.sweep_amount), 0) * 100) / 100,
    [rows, selected],
  );
  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const confirm = async ({ amount_moved, moved_on, override_reason }) => {
    try {
      const r = await api.post('/api/money/allocate/confirm', {
        transaction_ids: [...selected],
        amount_moved: Number(amount_moved),
        moved_on,
        override_reason,
      });
      toast.success(`Recorded: ${r.stamped.length} row(s) allocated, ${fmtCents(r.moved)} on ${moved_on}${r.override ? ' (override)' : ''}.`);
      await refresh();
      return { ok: true };
    } catch (e) {
      if (e.status === 409 && e.body?.code === 'SWEEP_AMOUNT_MISMATCH') return { ok: false, mismatch: e.body };
      toast.error(e.body?.error || e.message || 'Could not record the allocation');
      await refresh();
      return { ok: false };
    }
  };

  return (
    <div className="p-4">
      <div>
        <h2 className="text-xl font-semibold text-text">Allocate</h2>
        <p className="mt-1 text-sm text-text-muted">
          Swept, not yet moved to income. Computed from the rows below; the bank balance is not read here.
        </p>
      </div>

      {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}
      {data === null && !error ? <p className="mt-4 text-sm text-text-muted">Loading&hellip;</p> : null}

      {data ? (
        <>
          <div className="mt-4 font-mono text-sm text-text">
            In the sweep account (computed) {data.ready_count} row{data.ready_count === 1 ? '' : 's'} · {fmtCents(data.ready_total)}
            {data.amount_provisional ? <span className="ml-3 text-danger">Provisional: {data.held_count} swept row(s) carry no rate</span> : null}
          </div>

          {rows.length === 0 ? <p className="mt-4 text-sm text-text-muted">Nothing to allocate.</p> : (
            <ul className="mt-3 divide-y divide-border rounded-card border border-border bg-surface px-3">
              {rows.map((row) => (
                <li key={row.id} className="flex items-start gap-3 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(row.id)}
                    onChange={() => toggle(row.id)}
                    disabled={row.sweep_amount == null}
                    className="mt-1 h-4 w-4"
                    aria-label={`include ${row.counterparty || row.crm_deal_id}`}
                  />
                  <div className="min-w-0 flex-1 text-sm">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-medium text-text">{row.counterparty || 'counterparty not on file'}</span>
                      <span className="font-mono text-xs text-text-dim">{row.reference || 'no reference'}</span>
                      <a href={crmDeepLink('Deals', row.crm_deal_id)} target="_blank" rel="noreferrer" className="text-xs text-text-muted hover:underline">deal</a>
                    </div>
                    <div className="mt-0.5 font-mono text-[11.5px] text-text-muted">
                      swept {row.swept_at ? row.swept_at.slice(0, 10) : 'day unknown'} · premium {fmtCents(row.premium)} · rate {fmtPct(row.commission_rate)} × {row.commission_share ?? 'not stated'} · commission {fmtCents(row.commission)} · fee {fmtCents(row.fee)}
                    </div>
                  </div>
                  <div className="shrink-0 font-mono text-sm text-text">{fmtCents(row.sweep_amount)}</div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4">
            <MoveConfirm
              computedTotal={selectedTotal}
              count={selected.size}
              verb="allocation"
              destination="income"
              disabled={rows.length === 0}
              onConfirm={confirm}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}
